import { useCallback } from 'react'
import {
  DEFAULT_THEME,
  THEME_STORAGE_KEY,
  isTheme,
  useThemeStore,
  type Theme,
} from '@/stores/theme-store'

export function readStoredTheme(): Theme {
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
  if (isTheme(stored)) return stored
  // First-time visitor: honour OS preference if the browser exposes it.
  if (typeof window.matchMedia === 'function') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : DEFAULT_THEME
  }
  return DEFAULT_THEME
}

export function applyThemeToDocument(theme: Theme): void {
  // Light mode is delivered via :root, so the attribute is only needed for dark.
  if (theme === 'dark') {
    document.documentElement.setAttribute('data-theme', 'dark')
  } else {
    document.documentElement.removeAttribute('data-theme')
  }
}

export function persistTheme(theme: Theme): void {
  window.localStorage.setItem(THEME_STORAGE_KEY, theme)
}

export function initializeTheme(): void {
  const theme = readStoredTheme()
  useThemeStore.setState({ theme })
  applyThemeToDocument(theme)
}

export function useSetTheme(): (theme: Theme) => void {
  const setTheme = useThemeStore((s) => s.setTheme)
  return useCallback(
    (theme: Theme): void => {
      setTheme(theme)
      applyThemeToDocument(theme)
      persistTheme(theme)
    },
    [setTheme],
  )
}
